import { useState } from 'react'
import { useMutation } from '@apollo/client'
import { Plus, RefreshCw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button as ButtonComponent } from '@/components/ui/button'
import HelperDialog, { HelperDialogLoading } from '@/components/campaigns/HelperDialog'
import LicenseOverviewForm, { LicenseOverview } from '@/components/campaigns/LicenseOverviewForm'
import SystemPromptEditor, {
    CustomSystemPromptBadge,
    EditSystemPromptButton,
} from '@/components/campaigns/SystemPromptEditor'
import { SUGGEST_LICENSE_KEYWORDS } from '@/graphql/ads/prompt-suggestion-queries'
import { errorCodeOf, LicenseDraft, OVERVIEW_REQUIRED, useLicenseOverviewStep } from '@/hooks/useLicenseOverviewStep'
import {
    SystemPromptChange,
    systemPromptVariableOf,
    useSuggestionSystemPrompt,
} from '@/hooks/useSuggestionSystemPrompt'
import { ButtonProps } from '@/types'

// Type cast for the Button component from JSX file
const Button = ButtonComponent as React.FC<
    ButtonProps & Pick<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type' | 'aria-label'>
>

interface SuggestLicenseKeywordsData {
    suggestLicenseKeywords: string[]
}

interface KeywordHelperProps {
    /** The saved license (Edit License) */
    campaignId?: string | null
    /** The license that is not saved yet (Add License); the helper is disabled without this or campaignId */
    draft?: LicenseDraft
    /** Keywords currently in the form, saved or not */
    keywords: string[]
    onAdd: (keyword: string) => void
    maxKeywords?: number
    disabled?: boolean
    className?: string
}

/**
 * "Help me with keywords" for Add and Edit License: opens a dialog with the license overview form (the same one the AI
 * prompt helper uses), prefilled with the saved or typed data, then asks the backend for keywords grounded in it.
 * Keywords already in the form are never suggested; each suggestion is added with a click until the form holds
 * `maxKeywords`, and the dialog stays open until "Done" so several can be added. "Edit system prompt" shows the
 * instructions the keywords are written with, to edit and regenerate. Closing it ends the flow, so the next click
 * starts again from the form.
 */
const KeywordHelper = ({
    campaignId,
    draft,
    keywords,
    onAdd,
    maxKeywords = 5,
    disabled = false,
    className,
}: KeywordHelperProps) => {
    const {
        overview,
        formOpen,
        setFormOpen,
        loading: overviewLoading,
        open: openOverview,
        saved: overviewSaved,
        targetOf,
        available,
    } = useLicenseOverviewStep(campaignId, draft)
    const [dialogOpen, setDialogOpen] = useState(false)
    const [suggestions, setSuggestions] = useState<string[]>([])
    const [listed, setListed] = useState(false)
    const [failed, setFailed] = useState(false)
    const [empty, setEmpty] = useState(false)
    const [suggest, { loading }] = useMutation<SuggestLicenseKeywordsData>(SUGGEST_LICENSE_KEYWORDS)
    const systemPrompt = useSuggestionSystemPrompt({ campaignId, kind: 'KEYWORDS', active: dialogOpen })

    const inForm = new Set(keywords.map((keyword) => keyword.toLowerCase()))
    const visible = suggestions.filter((keyword) => !inForm.has(keyword.toLowerCase()))
    const full = keywords.length >= maxKeywords

    const requestSuggestions = async (
        exclude: string[],
        current: LicenseOverview | null = overview,
        override: string | null = systemPrompt.override,
    ) => {
        setFailed(false)
        setEmpty(false)
        try {
            const { data } = await suggest({
                variables: { ...targetOf(current), exclude, ...systemPromptVariableOf(override) },
            })
            const received = data?.suggestLicenseKeywords ?? []
            setSuggestions(received)
            setListed(received.length > 0)
            setEmpty(received.length === 0)
        } catch (error) {
            setSuggestions([])
            setListed(false)
            if (errorCodeOf(error) === OVERVIEW_REQUIRED) setFormOpen(true)
            else setFailed(true)
        }
    }

    const handleHelp = async () => {
        setDialogOpen(true)
        setFailed(false)
        setEmpty(false)
        setSuggestions([])
        setListed(false)
        if (!(await openOverview())) setFailed(true)
    }

    /** Closes the dialog and clears the flow, so the next click starts again from the overview form. */
    const close = () => {
        setDialogOpen(false)
        setFormOpen(false)
        setSuggestions([])
        setListed(false)
        setFailed(false)
        setEmpty(false)
        systemPrompt.reset()
    }

    const handleOverviewSaved = (saved: LicenseOverview) => {
        overviewSaved(saved)
        requestSuggestions(keywords, saved)
    }

    const handleSystemPromptApplied = (change: SystemPromptChange) =>
        requestSuggestions(keywords, overview, systemPrompt.apply(change))

    const handleAdd = (keyword: string) => {
        onAdd(keyword)
        setSuggestions((current) => current.filter((suggestion) => suggestion !== keyword))
    }

    const busy = loading || overviewLoading

    return (
        <div className={className}>
            <Button
                type="button"
                size="sm"
                className="tw:!mt-3"
                onClick={handleHelp}
                disabled={disabled || busy || !available || full}
            >
                Help me with keywords
            </Button>

            <HelperDialog
                open={dialogOpen}
                onOpenChange={(open) => !open && close()}
                title="Help me with keywords"
                footer={
                    listed &&
                    !systemPrompt.editing && (
                        <Button type="button" size="sm" onClick={close}>
                            Done
                        </Button>
                    )
                }
            >
                {overviewLoading ? (
                    <HelperDialogLoading label="Loading the license overview…" />
                ) : formOpen && available ? (
                    <LicenseOverviewForm
                        bordered={false}
                        campaignId={campaignId ?? undefined}
                        initialValues={overview}
                        submitLabel="Suggest keywords"
                        onSaved={handleOverviewSaved}
                        onCancel={close}
                    />
                ) : systemPrompt.editing ? (
                    <SystemPromptEditor
                        kind="KEYWORDS"
                        target={targetOf()}
                        override={systemPrompt.override}
                        onCancel={systemPrompt.cancel}
                        onApplied={handleSystemPromptApplied}
                    />
                ) : loading ? (
                    <HelperDialogLoading label="Generating suggestions…" />
                ) : (
                    <>
                        {failed && (
                            <p role="alert" className="tw:text-sm tw:text-red-400 tw:m-0">
                                We could not suggest keywords right now. Please try again.
                            </p>
                        )}

                        {empty && (
                            <p className="tw:text-sm tw:text-gray-400 tw:m-0">
                                There are no suggestions right now. Please try again.
                            </p>
                        )}

                        {(failed || empty) && systemPrompt.custom && (
                            <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2 tw:mt-2">
                                <CustomSystemPromptBadge />
                                <EditSystemPromptButton onClick={systemPrompt.edit} />
                            </div>
                        )}

                        {listed && (
                            <div>
                                <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-2 tw:mb-2">
                                    <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
                                        <span className="tw:text-sm tw:font-semibold">Pick keywords to add</span>
                                        {systemPrompt.custom && <CustomSystemPromptBadge />}
                                    </div>
                                    <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-1">
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => {
                                                setSuggestions([])
                                                setListed(false)
                                                setFormOpen(true)
                                            }}
                                        >
                                            Edit overview
                                        </Button>
                                        <EditSystemPromptButton onClick={systemPrompt.edit} />
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => requestSuggestions([...keywords, ...visible])}
                                            disabled={loading}
                                        >
                                            <RefreshCw size={14} />
                                            More ideas
                                        </Button>
                                    </div>
                                </div>

                                {(overview?.primaryUrl || Boolean(overview?.socialUrls?.length)) &&
                                    overview?.brandProfileStatus === 'failed' && (
                                        <p className="tw:text-xs tw:text-gray-400 tw:mt-0 tw:mb-2">
                                            We could not read the link, so these keywords use the rest of the license
                                            details.
                                        </p>
                                    )}

                                {visible.length > 0 ? (
                                    <ul
                                        aria-label="Keyword suggestions"
                                        className="tw:flex tw:flex-wrap tw:gap-2 tw:list-none tw:!p-0 tw:m-0"
                                    >
                                        {visible.map((keyword) => (
                                            <li key={keyword}>
                                                <button
                                                    type="button"
                                                    aria-label={`Add keyword: ${keyword}`}
                                                    onClick={() => handleAdd(keyword)}
                                                    disabled={full}
                                                    className={cn(
                                                        'tw:inline-flex tw:items-center tw:gap-1 tw:!rounded-full tw:border tw:border-gray-600 tw:bg-transparent tw:px-3 tw:py-1 tw:text-sm tw:text-foreground tw:transition-colors',
                                                        'tw:hover:border-blue-400 tw:focus-visible:border-blue-400 tw:disabled:opacity-50 tw:disabled:hover:border-gray-600',
                                                    )}
                                                >
                                                    <Plus size={14} />
                                                    {keyword}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="tw:text-sm tw:text-gray-400 tw:m-0">
                                        All the suggested keywords are added. Use More ideas for others.
                                    </p>
                                )}

                                {full && (
                                    <p className="tw:text-xs tw:text-gray-400 tw:mt-2 tw:mb-0">
                                        Maximum {maxKeywords} keywords reached. Remove one to add another.
                                    </p>
                                )}
                            </div>
                        )}
                    </>
                )}
            </HelperDialog>
        </div>
    )
}

export default KeywordHelper
